import { API_BASE_URL } from '@/api/config';
import type { AuthResponse, AuthUser, LoginFormValues, RegisterCustomerFormValues } from './types';

export class ApiError extends Error {
  /// `details` is what the backend sends next to `error` for errors the caller acts on, such as
  /// the `fieldErrors` of a form (#229).
  constructor(
    public status: number,
    message: string,
    public details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  const data = (await response.json().catch(() => ({}))) as T | { error?: string };

  if (!response.ok) {
    const errorMessage =
      typeof data === 'object' && data && 'error' in data && data.error
        ? data.error
        : 'Request failed';
    throw new ApiError(response.status, errorMessage);
  }

  return data as T;
}

/// A new account comes back signed in. Claiming an account created at the counter does not: the
/// way in is the activation mail (ADR 0008).
export type RegisterCustomerResponse = { message: string; email: string } & Partial<AuthResponse>;

export async function registerCustomerRequest(
  values: RegisterCustomerFormValues,
): Promise<RegisterCustomerResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(values),
  });

  return parseJsonResponse(response);
}

export async function resendVerificationRequest(email: string): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE_URL}/api/auth/email-verification/resend`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  return parseJsonResponse(response);
}

export async function confirmVerificationRequest(token: string): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE_URL}/api/auth/email-verification/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });

  return parseJsonResponse(response);
}

/** The single login for staff and buyers (#271); the role in the answer says where to go. */
export async function loginRequest(values: LoginFormValues): Promise<AuthResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(values),
  });

  return parseJsonResponse<AuthResponse>(response);
}

export async function getCurrentUserRequest(token: string): Promise<AuthUser> {
  const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await parseJsonResponse<{ user: AuthUser }>(response);
  return data.user;
}

export type AccountActivation = { email: string; fullName: string; needsPhone: boolean };

/** Who an invitation of a counter account is for, and whether it still needs a phone. */
export async function getAccountActivationRequest(token: string): Promise<AccountActivation> {
  const response = await fetch(
    `${API_BASE_URL}/api/auth/account-activation?token=${encodeURIComponent(token)}`,
  );

  return parseJsonResponse(response);
}

/** Activates a counter account: verifies the email, sets the password and signs in. */
export async function activateAccountRequest(values: {
  token: string;
  password: string;
  phone?: string;
}): Promise<AuthResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/account-activation`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(values),
  });

  return parseJsonResponse<AuthResponse>(response);
}

/** "Forgot my password": always answers the same, whether the account exists or not. */
export async function requestPasswordResetRequest(email: string): Promise<{ message: string }> {
  const response = await fetch(`${API_BASE_URL}/api/auth/password-reset/request`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  return parseJsonResponse(response);
}

/** Whose password a reset link changes. Fails with 400 for a link that no longer works. */
export async function getPasswordResetRequest(token: string): Promise<{ email: string }> {
  const response = await fetch(
    `${API_BASE_URL}/api/auth/password-reset?token=${encodeURIComponent(token)}`,
  );

  return parseJsonResponse(response);
}

/** Sets the new password from a reset link and signs in. */
export async function resetPasswordRequest(values: {
  token: string;
  password: string;
}): Promise<AuthResponse> {
  const response = await fetch(`${API_BASE_URL}/api/auth/password-reset`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(values),
  });

  return parseJsonResponse<AuthResponse>(response);
}
